import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { publicApi } from "@/lib/api";
import { Reveal } from "@/components/motion/Reveal";

const PLACEHOLDER_IMG = "https://images.unsplash.com/photo-1624957485467-f33854e1ad85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3ODQ3NTQyNTJ8MA&ixlib=rb-4.1.0&q=85";

const FALLBACK_CASES = [
  {
    id: "f1", slug: "manufactura-iso-9001",
    sector: "Manufactura", titulo: "Implementación ISO 9001 en planta de producción",
    empresa: "Empresa del sector manufactura",
    situacion: "Procesos sin estandarizar, alta variabilidad y ausencia de indicadores clave.",
    accion: "Diagnóstico integral, diseño de mapa de procesos e implementación del sistema.",
    resultado: "Certificación ISO 9001 obtenida en primer intento y reducción del 32% en no conformidades.",
    norma: "ISO 9001", image: PLACEHOLDER_IMG,
  },
  {
    id: "f2", slug: "alimentaria-iso-14001",
    sector: "Industria alimentaria", titulo: "Sistema de Gestión Ambiental ISO 14001",
    empresa: "Empresa del sector alimentario",
    situacion: "Cumplimiento parcial de requisitos legales ambientales y desperdicios sin trazabilidad.",
    accion: "Identificación de aspectos e impactos, matriz legal y controles operacionales.",
    resultado: "Reducción del 24% en generación de residuos y certificación en 8 meses.",
    norma: "ISO 14001", image: PLACEHOLDER_IMG,
  },
  {
    id: "f3", slug: "tecnologia-iso-27001",
    sector: "Tecnología", titulo: "Sistema de Gestión de Seguridad de la Información",
    empresa: "Empresa del sector tecnológico",
    situacion: "Requerimientos de clientes internacionales y ausencia de política formal de seguridad.",
    accion: "Evaluación de riesgos, declaración de aplicabilidad y controles Anexo A.",
    resultado: "Certificación ISO/IEC 27001 y acceso a nuevos contratos corporativos.",
    norma: "ISO/IEC 27001", image: PLACEHOLDER_IMG,
  },
];

export default function CasesPreview() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    publicApi.casos({ destacado: true }).then((r) => {
      setItems(r.length ? r : FALLBACK_CASES);
      setLoaded(true);
    }).catch(() => {
      setItems(FALLBACK_CASES);
      setLoaded(true);
    });
  }, []);

  const list = items.slice(0, 3);

  return (
    <section id="casos-preview" className="relative py-24 md:py-36 border-t border-[#1A1A1A]/10" data-testid="cases-preview-section">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="grid grid-cols-12 gap-6 items-end mb-14">
          <Reveal className="col-span-12 md:col-span-8">
            <div className="eyebrow mb-6">— 05 · Casos</div>
            <h2 className="display-2">Resultados que <em className="italic text-[#1B3B2B]">generan confianza</em>.</h2>
          </Reveal>
          <Reveal delay={0.15} className="col-span-12 md:col-span-4 md:text-right">
            <Link to="/casos-de-exito" className="kb-btn ghost" data-testid="cases-see-all">
              Conocer todos los casos <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {list.map((c, i) => (
            <motion.article
              key={c.id}
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ delay: i * 0.1, duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
              className="group flex flex-col"
              data-testid={`case-preview-${i}`}
            >
              <Link to={`/casos-de-exito/${c.slug}`} className="block">
                <div className="relative aspect-[4/5] overflow-hidden bg-[#EFEBE2]">
                  <img
                    src={c.image || c.imagen || PLACEHOLDER_IMG}
                    alt={c.titulo}
                    className="absolute inset-0 w-full h-full object-cover duotone transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4 text-[10px] tracking-[0.22em] uppercase text-white/90 bg-[#1A1A1A]/50 backdrop-blur px-3 py-1.5">
                    {c.sector}
                  </div>
                </div>
                <div className="mt-6">
                  <div className="eyebrow text-[#1B3B2B]">{c.norma}</div>
                  <h3 className="mt-3 font-serif text-2xl md:text-3xl leading-tight text-[#1A1A1A] group-hover:text-[#1B3B2B] transition-colors">{c.titulo}</h3>
                  <p className="mt-2 text-sm text-[#4A4A4A]">{c.empresa}</p>
                </div>
                <div className="mt-5 space-y-3 text-sm text-[#1A1A1A]/80">
                  <div><span className="eyebrow text-[#1A1A1A]/50">Situación · </span>{c.situacion}</div>
                  <div><span className="eyebrow text-[#1B3B2B]">Resultado · </span>{c.resultado}</div>
                </div>
                <div className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase link-underline">
                  Ver caso completo <ArrowUpRight className="w-3 h-3" />
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
